import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";

import { getImpersonationParams } from "@stadium/helpers/impersonation";

const emptyObj = {};

type RequestOptions<B extends boolean | undefined = false> = AxiosRequestConfig & {
  bypassSerialisation?: B;
};

type ApiResponse<ResponseParams, B extends boolean | undefined> = B extends true
  ? AxiosResponse<ResponseParams, any>
  : ResponseParams;

const apiDataCallback =
  <ResponseParams, B extends boolean | undefined>(bypassSerialisation?: B) =>
  (res: AxiosResponse<ResponseParams, any>): ApiResponse<ResponseParams, B> =>
    (bypassSerialisation ? res : res.data) as ApiResponse<ResponseParams, B>;

const getInstance = ({
  baseURL,
  timeout = 55000,
  headers,
  fetchOptions,
  ...rest
}: axios.CreateAxiosDefaults) => {
  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      ...headers,
      ...getImpersonationParams(),
    },
    fetchOptions: { cache: "no-store", ...fetchOptions },
    ...rest,
  });

  return {
    get: <ResponseParams = any, B extends boolean | undefined = false>(
      url: string,
      params?: AxiosRequestConfig<any>["params"],
      options: RequestOptions<B> = {},
    ): Promise<ApiResponse<ResponseParams, B>> => {
      const { bypassSerialisation, ...requestOptions } = options;
      return instance
        .get<ResponseParams>(url, { params, ...requestOptions })
        .then(apiDataCallback(bypassSerialisation));
    },
    post: <ResponseParams = any, RequestParams = any, B extends boolean | undefined = false>(
      url: string,
      data?: RequestParams,
      params: AxiosRequestConfig<any>["params"] = emptyObj,
      options: RequestOptions<B> = {},
    ): Promise<ApiResponse<ResponseParams, B>> => {
      const { bypassSerialisation, ...requestOptions } = options;
      return instance
        .post<ResponseParams>(url, data, { params, ...requestOptions })
        .then(apiDataCallback(bypassSerialisation));
    },
    put: <ResponseParams = any, RequestParams = any>(url: string, data?: RequestParams) =>
      instance.put<ResponseParams>(url, data).then(apiDataCallback()),
    delete: <ResponseParams = any, B extends boolean | undefined = false>(
      url: string,
      params?: AxiosRequestConfig<any>["params"],
      options: RequestOptions<B> = {},
    ): Promise<ApiResponse<ResponseParams, B>> => {
      const { bypassSerialisation, ...requestOptions } = options;
      return instance
        .delete<ResponseParams>(url, { params, ...requestOptions })
        .then(apiDataCallback(bypassSerialisation));
    },
    patch: <ResponseParams = any, RequestParams = any>(
      url: string,
      data?: RequestParams,
      params: AxiosRequestConfig<any>["params"] = emptyObj,
    ) => instance.patch<ResponseParams>(url, data, { params }).then(apiDataCallback()),
    putWithFile: (url, data, params) =>
      instance
        .put(url, data, { headers: { "Content-type": "multipart/form-data" }, params })
        .then(apiDataCallback()),
    postWithFile: (url, data, params) =>
      instance
        .post(url, data, { headers: { "Content-type": "multipart/form-data" }, params })
        .then(apiDataCallback()),
    getFile: <B extends boolean | undefined = false>(
      url,
      params = emptyObj,
      options: RequestOptions<B> = {},
    ): Promise<ApiResponse<Blob, B>> => {
      const { bypassSerialisation, ...requestOptions } = options;
      return instance
        .get(url, {
          params: { ...params, skipFormat: true },
          ...requestOptions,
          responseType: "blob",
        })
        .then(apiDataCallback(bypassSerialisation));
    },

    addHeader: (key, value) => {
      instance.defaults.headers.common[key] = value;
    },
    removeHeader: (key) => {
      delete instance.defaults.headers.common[key];
    },
    interceptors: instance.interceptors,
    _instance: instance,
  };
};

export const ORDER_SERVICE_API_BASE_URL = process.env.NEXT_PUBLIC_SM_API_HOST;
export const ORDER_SERVICE_API_URL = `${ORDER_SERVICE_API_BASE_URL}/api/v1/ams`;
export const PRODUCT_SERVICE_GRAPHQL_API_BASE_URL =
  process.env.NEXT_PUBLIC_PRODUCTS_GRAPHQL_API_BASE_URL;
export const USER_SERVICE_GRAPHQL_API_BASE_URL = process.env.NEXT_PUBLIC_USER_SERVICE_API_BASE_URL;
export const SEARCH_SERVICE_GRAPHQL_API_BASE_URL = process.env.NEXT_PUBLIC_SEARCH_API_BASE_URL;

export const orderServiceInstance = getInstance({ baseURL: ORDER_SERVICE_API_URL });

const GRAPHQL_HEADERS =
  typeof window === "undefined"
    ? { "User-Agent": `${process.env.NEXT_PUBLIC_DOMAIN}-server` }
    : undefined;

export const productServiceGraphqlInstance = getInstance({
  baseURL: PRODUCT_SERVICE_GRAPHQL_API_BASE_URL,
  headers: GRAPHQL_HEADERS,
});
export const userGraphqlInstance = getInstance({
  baseURL: USER_SERVICE_GRAPHQL_API_BASE_URL,
  headers: GRAPHQL_HEADERS,
});
export const searchGraphqlInstance = getInstance({
  baseURL: SEARCH_SERVICE_GRAPHQL_API_BASE_URL,
  headers: GRAPHQL_HEADERS,
});

export { getInstance };
